import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Theme
import theme from './theme';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';

// Layout
import Layout from './components/Layout/Layout';
import PrivateRoute from './components/PrivateRoute';
import AuthMonitorComponent from './components/AuthMonitor';
import DevelopmentBanner from './components/DevelopmentBanner';

// Pages
import Homepage from './pages/Homepage';
import About from './pages/About';
import Contact from './pages/Contact';
import Pricing from './pages/Pricing';
import SubscriptionManagement from './pages/SubscriptionManagement';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import ProjectOnboarding from './pages/ProjectOnboarding';
import Reels from './pages/Reels';
import ReelCreate from './pages/ReelCreate';
import ReelDetail from './pages/ReelDetail';
import Publishing from './pages/Publishing';
import Analytics from './pages/Analytics';
import Schedules from './pages/Schedules';
import Settings from './pages/Settings';
import NicheTemplates from './pages/NicheTemplates';
import NotFoundPage from './pages/NotFoundPage';
import NewLandingPage from './pages/NewLandingPage';
import SmartLandingPage from './components/SmartLandingPage';
import VerifyMagicLink from './pages/VerifyMagicLink';

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        {/* You could add a loading spinner here if needed */}
      </Box>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-magic-link" element={<VerifyMagicLink />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      
      {/* A/B test variants */}
      <Route path="/new-landing" element={<NewLandingPage />} />
      <Route path="/ab-test" element={<SmartLandingPage />} />
      
      {/* Homepage for unauthenticated users, dashboard for authenticated */}
      <Route 
        path="/" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <SmartLandingPage />
        } 
      />
      
      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Dashboard />} />
      </Route>
      
      <Route
        path="/projects"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Projects />} />
        <Route path="new" element={<ProjectOnboarding />} />
        <Route path=":id" element={<ProjectDetail />} />
      </Route>
      
      <Route
        path="/reels"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Reels />} />
        <Route path="create" element={<ReelCreate />} />
        <Route path=":id" element={<ReelDetail />} />
      </Route>
      
      <Route
        path="/publishing"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Publishing />} />
      </Route>
      
      <Route
        path="/analytics"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Analytics />} />
      </Route>
      
      <Route
        path="/schedules"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Schedules />} />
      </Route>
      
      <Route
        path="/templates"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<NicheTemplates />} />
      </Route>

      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Settings />} />
      </Route>
      
      <Route
        path="/subscription"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<SubscriptionManagement />} />
      </Route>
      
      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <DevelopmentBanner />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <SnackbarProvider 
          maxSnack={3}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          <Router>
            <AuthProvider>
              <AuthMonitorComponent />
              <SubscriptionProvider>
                <ProjectProvider>
                  <AppRoutes />
                </ProjectProvider>
              </SubscriptionProvider>
            </AuthProvider>
          </Router>
        </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;